<script setup lang="ts">
import { computed, type PropType, ref, toRef } from 'vue';
import Dialog from 'primevue/dialog';
import ToggleSwitch from 'primevue/toggleswitch';
import { ButtonType, CustomButton } from '@/components';
import { type FormField, FormFieldChoice } from '@/models';
import InputNumber from 'primevue/inputnumber';
import InputText from 'primevue/inputtext';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  field: {
    type: Object as PropType<FormField>,
    required: true
  }
});
const localField = toRef(props.field);

const emits = defineEmits(['onClose', 'onSave']);

const closeDialog = () => {
  emits('onClose');
};

const enableRecode = ref(false);
const choices = ref<FormFieldChoice[]>(JSON.parse(JSON.stringify(props.field.options.choices!)));

const isPristine = computed(() => JSON.stringify(props.field.options.choices) !== JSON.stringify(choices.value));

const saveCodes = () => {
  localField.value.options.choices = choices.value;
  emits('onClose');
};

</script>

<template>
    <div class="dialog-root">
        <Dialog :visible="visible"
            header="Recode values"
            modal
            class="dialog"
            append-to="self"
            @update:visible="closeDialog">
            <div class="dialog-content">
              <div class="warning-block">
                <h2>
                  <FontAwesomeIcon :icon="[`far`, `exclamation-triangle`]"
                    class="exclamation-icon" />
                  Risk of data loss
                </h2>
                <p>
                  If you change the value of a choice that has already been used for
                  data collection, you will lose that data.
                </p>
                <div class="recode-toggle">
                  <div class="recode-toggle-label">Enable recode values</div>
                  <ToggleSwitch v-model="enableRecode"/>
                </div>
              </div>
              <div class="choices">
                <div v-for="choice in choices" :key="choice.id" class="choice-item">
                  <div class="choice-label">{{ choice.label }}</div>
                  <div class="inputs">
                    <InputNumber v-model="choice.value as number"
                      :disabled="!enableRecode"/>
                    <InputText v-model="choice.variableName"
                      class="choice-variable-name-input"
                      placeholder="Enter a unique variable name"/>
                  </div>
                </div>
              </div>
            </div>
            <div class="footer">
              <div class="empty"></div>
              <div class="footer-buttons">
                <CustomButton text="Cancel"
                  @onClick="closeDialog"
                  :margin-left="16"
                  :type="ButtonType.OUTLINED"/>
                <CustomButton text="Save"
                  @onClick="saveCodes"
                  :disabled="!isPristine"
                  :margin-left="16"
                  :type="ButtonType.POSITIVE"/>
              </div>
            </div>
        </Dialog>
    </div>
</template>

<style scoped lang="scss">
@import "../../../assets/dialog";
:deep(.p-dialog) {
  max-width: 800px;
}
:deep(.p-dialog-header) {
  padding: unset;
  padding: 40px 40px 0px 40px;
}
:deep(.p-dialog-content) {
  padding: unset;
}
.dialog-content {
  padding: 20px 40px 24px;
}
.warning-block {
  margin-bottom: 20px;
  padding: 12px 0 0 12px;
  border: 1px solid #ea3546;
  border-radius: 4px;
  background-color: #fef5f6;
  color: #5c0a11;
  h2 {
    font-size: 14px;
    font-weight: 600;
    line-height: 24px;
    margin: 0 0 4px 0;
    .exclamation-icon {
      color: #ea3546;
      line-height: 14px;
      font-size: 16px;
      margin-right: 8px;
    }
  }
  p {
    margin: 0,
  }
  .recode-toggle {
    margin-top: 12px;
    margin-bottom: 12px;
    display: flex;
    align-items: center;

    .recode-toggle-label {
      margin-right: 12px;
    }
  }
}
.choices {
  margin-top: 20px;
  .choice-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  .choice-label {
    font-weight: 500;
  }
  .inputs {
    display: flex;
    align-items: center;
  }
  .choice-variable-name-input {
    margin-left: 12px;
  }
  :deep(.p-inputnumber-input) {
    width: 100px;
  }
}
.footer {
  padding: 12px 40px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #ced3d9;
  background-color: #f5f7f7;

  .footer-buttons {
    display: flex;
  }
}
</style>
